import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  useTheme,
  Alert,
  Divider,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert as MuiAlert
} from '@mui/material';
import {
  Add as AddIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  LocalHospital as LocalHospitalIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  ExitToApp as ExitToAppIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
// Removed Redux auth selectors - now using useAuth() hook
import { useAuth } from '../../context/AuthContext';
import { updateEncounter } from '../../redux/slices/encountersSlice';
import { dischargePatientFromBed, getCurrentBedAssignment } from '../../services/bedAssignmentService';
import { dischargePatientFromRoom, getCurrentRoomAssignment } from '../../services/roomAssignmentService';
import DischargeService from '../../services/dischargeService';
import { DatePicker } from '@mui/x-date-pickers';

const DischargeActivity = ({ encounter, patient, formData, setFormData }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user, loading } = useAuth();
  // const user = useAuth().user; // Now using useAuth() hook
const [clinicalImpression, setClinicalImpression] = useState('');
  const [bedRequestType, setBedRequestType] = useState('');
  const [disposition, setDisposition] = useState('Discharge');
  const [followUpSuggestions, setFollowUpSuggestions] = useState([]);
  const [dischargeComments, setDischargeComments] = useState('');
  const [dischargeDate, setDischargeDate] = useState(new Date().toISOString().split('T')[0]);
  const [dischargeTime, setDischargeTime] = useState(new Date().toTimeString().slice(0,5));
  
  // Discharge process state
  const [isDischarging, setIsDischarging] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Mock data for demonstration
  const bedRequestOptions = [
    'ED Admit Decision',
    'ED Transfer to L&D', 
    'Place Patient in ED Psych'
  ];

  const dispositionOptions = [
    'Discharge',
    'Transfer to Another Facility',
    'HBR ED to UNC ED',
    'AMA',
    'Eloped',
    'Expired',
    'LWBS after Triage'
  ];

  const followUpSuggestionsData = [
    'PCP - None Specified',
    'Other - Lookup',
    'Other - Free Text'
  ];

  const rdcReferralData = {
    schedulers: 'Send message to RDC schedulers (check yes)',
    location: 'GOLDSBORO/KINSTON REGION - 204 Cox Blvd, Goldsboro, NC...',
    patientPreferred: 'GOLDSBORO/KINSTON REGION - 204 Cox Blvd, Goldsboro, NC...'
  };

  // Check if patient is already discharged
  const isAlreadyDischarged = encounter?.status === 'discharged' || encounter?.status === 'finished';

  const handleFinalizeDischarge = async () => {
    setIsDischarging(true);
    
    try {
      const dischargeDateTime = new Date(`${dischargeDate}T${dischargeTime}`);
      const dischargeService = new DischargeService();
      
      // Update encounter status and discharge details
      await dischargeService.dischargePatient(encounter.id, {
        status: 'discharged',
        endDate: dischargeDateTime,
        disposition: disposition,
        dischargeDate: dischargeDateTime,
        clinicalImpression: clinicalImpression,
        comments: dischargeComments,
        bedRequestType: bedRequestType,
        dischargedBy: user?.uid || 'system',
        dischargedByName: user?.displayName || user?.email || 'System'
      });

      // Update patient status if patient exists
      if (patient?.id) {
        await dischargeService.updatePatientDischargeStatus(patient.id, {
          isInpatient: false,
          admissionStatus: 'discharged',
          currentEncounterId: null
        });
      }

      // Release bed or room assignment
      try {
        // First check for bed assignment
        const bedAssignment = await getCurrentBedAssignment(patient?.id || encounter?.patientId);
        if (bedAssignment && bedAssignment.id) {
          await dischargePatientFromBed(
            bedAssignment.id,
            user?.uid || 'system',
            user?.role || 'system',
            `Discharged: ${disposition} - ${dischargeComments || 'No additional comments'}`
          );
        } else {
          // Check for room assignment (stand-alone rooms)
          const roomAssignment = await getCurrentRoomAssignment(patient?.id || encounter?.patientId);
          if (roomAssignment && roomAssignment.id) {
            await dischargePatientFromRoom(
              roomAssignment.id,
              user?.uid || 'system',
              user?.role || 'system',
              `Discharged: ${disposition} - ${dischargeComments || 'No additional comments'}`
            );
          }
        }
      } catch (assignmentErr) {
        console.warn('No bed/room assignment to release:', assignmentErr);
      }

      // Update Redux store
      await dispatch(updateEncounter({
        encounterId: encounter.id,
        updateData: {
          status: 'discharged',
          endDate: dischargeDateTime,
          dischargeDetails: {
            disposition: disposition,
            dischargeDate: dischargeDateTime,
            clinicalImpression: clinicalImpression,
            comments: dischargeComments,
            bedRequestType: bedRequestType,
            dischargedBy: user?.uid || 'system',
            dischargedByName: user?.displayName || user?.email || 'System',
            dischargedAt: new Date()
          }
        }
      })).unwrap();

      setSnackbarMessage(`Patient ${patient?.name || 'Unknown'} successfully discharged with disposition: ${disposition}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setConfirmDialogOpen(false);
      
    } catch (error) {
      console.error('Error finalizing discharge:', error);
      setSnackbarMessage('Failed to complete discharge. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsDischarging(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Discharge Status Alert */}
      {isAlreadyDischarged && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Patient Already Discharged</strong> - This encounter was completed on{' '}
            {encounter?.endDate ? new Date(encounter.endDate).toLocaleString() : 'Unknown Date'}{' '}
            with disposition: {encounter?.dischargeDetails?.disposition || 'Unknown'}
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} lg={8}>
          {/* Clinical Impression */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssignmentIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Clinical Impression
                </Typography>
                <Button 
                  variant="text" 
                  size="small" 
                  sx={{ ml: 'auto', color: theme.palette.primary.main }}
                  disabled={isAlreadyDischarged}
                >
                  Add from Problem List
                </Button>
              </Box>
              <TextField
                placeholder="Add a new impression"
                value={clinicalImpression}
                onChange={(e) => setClinicalImpression(e.target.value)}
                multiline
                rows={3}
                fullWidth
                sx={{ mb: 2 }}
                disabled={isAlreadyDischarged}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />} 
                  size="small"
                  disabled={isAlreadyDischarged}
                >
                  Add
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<PersonIcon />} 
                  size="small"
                  disabled={isAlreadyDischarged}
                >
                  Associate
                </Button>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Suggested by Chief Complaint
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No suggestions to display
                </Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Impressions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {clinicalImpression || 'No impressions to display'}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Bed Request */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalHospitalIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Bed Request
                </Typography>
                <Button 
                  variant="contained" 
                  size="small" 
                  startIcon={<AddIcon />}
                  sx={{ ml: 'auto' }}
                  disabled={isAlreadyDischarged}
                >
                  New Order
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {bedRequestOptions.map((option) => (
                  <Box key={option} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={bedRequestType === option}
                          onChange={(e) => setBedRequestType(option)}
                          size="small"
                          disabled={isAlreadyDischarged}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
                          {option}
                        </Typography>
                      }
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* PDMP Report */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  PDMP Report
                </Typography>
                <Chip 
                  label="▲" 
                  size="small" 
                  sx={{ 
                    ml: 1,
                    backgroundColor: theme.palette.warning.main,
                    color: 'white',
                    fontSize: '0.8rem'
                  }} 
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                PDMP report information will be displayed here
              </Typography>
            </CardContent>
          </Card>

          {/* ED/Orders/Rx/Amb Ref */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  ED/Orders/Rx/Amb Ref
                </Typography>
                <Button 
                  variant="contained" 
                  size="small" 
                  startIcon={<AddIcon />}
                  sx={{ ml: 'auto' }}
                  disabled={isAlreadyDischarged}
                >
                  New Order
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Orders and prescriptions will be displayed here
              </Typography>
            </CardContent>
          </Card>

          {/* Follow Up Nurse */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Follow Up Nurse
                </Typography>
                <Button 
                  variant="contained" 
                  size="small" 
                  sx={{ ml: 'auto' }}
                  disabled={isAlreadyDischarged}
                >
                  New
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Follow-up nurse information will be displayed here
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} lg={4}>
          {/* RDC Referral */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  RDC Referral
                </Typography>
                <Chip 
                  label="✓" 
                  size="small" 
                  sx={{ 
                    ml: 1,
                    backgroundColor: theme.palette.success.main,
                    color: 'white',
                    fontSize: '0.8rem'
                  }} 
                />
                <Button 
                  variant="contained" 
                  size="small" 
                  sx={{ ml: 'auto' }}
                  disabled={isAlreadyDischarged}
                >
                  New
                </Button>
              </Box>
              
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                03/21 1204
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={<Checkbox defaultChecked size="small" disabled={isAlreadyDischarged} />}
                  label={
                    <Typography variant="body2">
                      {rdcReferralData.schedulers}
                    </Typography>
                  }
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  RDC Referrals
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {rdcReferralData.location}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Patient-preferred RDC Location
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {rdcReferralData.patientPreferred}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Best Practice Advisories */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip 
                  label="BestPractice Advisories" 
                  sx={{ 
                    backgroundColor: theme.palette.warning.main, 
                    color: 'white', 
                    fontWeight: 'bold'
                  }} 
                />
                <Button 
                  variant="text" 
                  size="small" 
                  sx={{ ml: 'auto', color: theme.palette.primary.main }}
                  disabled={isAlreadyDischarged}
                >
                  Follow-Up Actions
                </Button>
              </Box>
              <Alert severity="warning" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  Allergies have not been verified during this encounter.
                </Typography>
              </Alert>
            </CardContent>
          </Card>

          {/* Work/School Excuse */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Work/School Excuse
                </Typography>
                <Button 
                  variant="contained" 
                  size="small" 
                  sx={{ ml: 'auto' }}
                  disabled={isAlreadyDischarged}
                >
                  New
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                No work/school excuse documents
              </Typography>
            </CardContent>
          </Card>

          {/* Follow-Up */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Follow-Up
                </Typography>
              </Box>
              
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Suggestions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                {followUpSuggestionsData.map((suggestion, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon sx={{ color: 'success.main', fontSize: '1rem' }} />
                    <Typography variant="body2">{suggestion}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* PDMP Report */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  PDMP Report
                </Typography>
                <Chip 
                  label="▲" 
                  size="small" 
                  sx={{ 
                    ml: 1,
                    backgroundColor: theme.palette.warning.main,
                    color: 'white',
                    fontSize: '0.8rem'
                  }} 
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                PDMP report information will be displayed here
              </Typography>
            </CardContent>
          </Card>

          {/* ED/Orders/Rx/Amb Ref */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  ED/Orders/Rx/Amb Ref
                </Typography>
                <Button 
                  variant="contained" 
                  size="small" 
                  startIcon={<AddIcon />}
                  sx={{ ml: 'auto' }}
                >
                  New Order
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Orders and prescriptions will be displayed here
              </Typography>
            </CardContent>
          </Card>

          {/* Follow Up Nurse */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Follow Up Nurse
                </Typography>
                <Button 
                  variant="contained" 
                  size="small" 
                  sx={{ ml: 'auto' }}
                >
                  New
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Follow-up nurse information will be displayed here
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} lg={4}>
          {/* RDC Referral */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  RDC Referral
                </Typography>
                <Chip 
                  label="✓" 
                  size="small" 
                  sx={{ 
                    ml: 1,
                    backgroundColor: theme.palette.success.main,
                    color: 'white',
                    fontSize: '0.8rem'
                  }} 
                />
                <Button 
                  variant="contained" 
                  size="small" 
                  sx={{ ml: 'auto' }}
                >
                  New
                </Button>
              </Box>
              
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                03/21 1204
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={<Checkbox defaultChecked size="small" />}
                  label={
                    <Typography variant="body2">
                      {rdcReferralData.schedulers}
                    </Typography>
                  }
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  RDC Referrals
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {rdcReferralData.location}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Patient-preferred RDC Location
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {rdcReferralData.patientPreferred}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Best Practice Advisories */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip 
                  label="BestPractice Advisories" 
                  sx={{ 
                    backgroundColor: theme.palette.warning.main, 
                    color: 'white', 
                    fontWeight: 'bold'
                  }} 
                />
                <Button 
                  variant="text" 
                  size="small" 
                  sx={{ ml: 'auto', color: theme.palette.primary.main }}
                >
                  Follow-Up Actions
                </Button>
              </Box>
              <Alert severity="warning" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  Allergies have not been verified during this encounter.
                </Typography>
              </Alert>
            </CardContent>
          </Card>

          {/* Work/School Excuse */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Work/School Excuse
                </Typography>
                <Button 
                  variant="contained" 
                  size="small" 
                  sx={{ ml: 'auto' }}
                >
                  New
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                No work/school excuse documents
              </Typography>
            </CardContent>
          </Card>

          {/* Follow-Up */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Follow-Up
                </Typography>
              </Box>
              
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Suggestions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                {followUpSuggestionsData.map((suggestion, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon sx={{ color: 'success.main', fontSize: '1rem' }} />
                    <Typography variant="body2">{suggestion}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Disposition */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalHospitalIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Disposition
                </Typography>
              </Box>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  value={disposition}
                  onChange={(e) => setDisposition(e.target.value)}
                  size="small"
                  disabled={isAlreadyDischarged}
                >
                  {dispositionOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Discharge Date & Time
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <DatePicker
                    label="Date"
                    value={dischargeDate ? new Date(dischargeDate) : null}
                    onChange={(date) => setDischargeDate(date ? date.toISOString().split('T')[0] : '')}
                    disabled={isAlreadyDischarged}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small' }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    type="time"
                    value={dischargeTime}
                    onChange={(e) => setDischargeTime(e.target.value)}
                    fullWidth
                    size="small"
                    disabled={isAlreadyDischarged}
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Comments
              </Typography>
              <TextField
                placeholder="Add discharge comments..."
                value={dischargeComments}
                onChange={(e) => setDischargeComments(e.target.value)}
                multiline
                rows={3}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                disabled={isAlreadyDischarged}
              />
            </CardContent>
          </Card>

          {/* Chart Status */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssignmentIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  Chart Status
                </Typography>
                <Button 
                  variant="text" 
                  size="small" 
                  sx={{ ml: 'auto', color: theme.palette.primary.main }}
                >
                  🔄
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <span style={{ fontWeight: 600 }}>Chart Status:</span> In Progress
                </Typography>
                <Typography variant="body2">
                  <span style={{ fontWeight: 600 }}>Last Updated:</span> {new Date().toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Finalize Discharge Button */}
      {!isAlreadyDischarged && (
        <Box sx={{ 
          position: 'sticky', 
          bottom: 0, 
          backgroundColor: 'white', 
          borderTop: 1, 
          borderColor: 'divider', 
          p: 2, 
          mt: 3,
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Button
            variant="contained"
            color="success"
            size="large"
            startIcon={<ExitToAppIcon />}
            onClick={() => setConfirmDialogOpen(true)}
            disabled={isDischarging || !disposition}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
              }
            }}
          >
            {isDischarging ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Finalizing Discharge...
              </>
            ) : (
              'Finalize Discharge'
            )}
          </Button>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ExitToAppIcon color="warning" />
          Confirm Patient Discharge
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to finalize the discharge for <strong>{patient?.name || 'this patient'}</strong>?
          </Typography>
          <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Discharge Summary:</Typography>
            <Typography variant="body2">• <strong>Disposition:</strong> {disposition}</Typography>
            <Typography variant="body2">• <strong>Date & Time:</strong> {new Date(`${dischargeDate}T${dischargeTime}`).toLocaleString()}</Typography>
            {clinicalImpression && (
              <Typography variant="body2">• <strong>Clinical Impression:</strong> {clinicalImpression}</Typography>
            )}
            {dischargeComments && (
              <Typography variant="body2">• <strong>Comments:</strong> {dischargeComments}</Typography>
            )}
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action will update the encounter status to "Discharged" and release any bed/room assignments. 
            This cannot be undone easily.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={isDischarging}>
            Cancel
          </Button>
          <Button 
            onClick={handleFinalizeDischarge} 
            variant="contained" 
            color="success"
            disabled={isDischarging}
            startIcon={isDischarging ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            {isDischarging ? 'Processing...' : 'Confirm Discharge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default DischargeActivity; 